import { useState } from "react";
import { useDispatch } from "react-redux";
import { AppDispatch } from "configureStore";
import { Button, Banner, Text } from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import { setDestroy, setIsModalxOpen } from "components/modalx/modalx.slice";
import { useParams } from "react-router-dom";
import { fetchFileDetails } from "containers/file/file.Slice";
import { fetchAuthorizedFileDetails } from "containers/file-authorized/authorized-file.Slice";
import { fetchCabinetSummaryThunk } from "features/cabinet-tasks/cabinet-summary.slice";
import { post } from "services/clicktosign.service";
import { fetchUserAssets } from "containers/user-assets/user-assets.slice";
import { fetchFuelReportDetails } from "containers/fuel-report-view-container/fuel-report-container.slice";
import { fetchBusinessTripDetails } from "containers/business-trip-view-container/business-trip-container.slice";
import { clickSignErrorKey } from "utils/click-sign-errors";

interface ConfirmModalProps {
  fileId: string;
  tripId?: string;
  companyId?: string;
  onSuccess?: () => void;
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({ fileId, tripId, companyId: propCompanyId, onSuccess }) => {
  const dispatch: AppDispatch = useDispatch();
  const { t } = useTranslation();
  const { companyId: paramCompanyId, reportId } = useParams();
  const companyId = propCompanyId || paramCompanyId;

  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleToSendRequest = async () => {
    try {
      setIsLoading(true);
      const payload = {
        fileId,
      };
      await post(companyId, payload);
      if (companyId && fileId) {
        dispatch(fetchFileDetails({ fileId, companyId }));
        dispatch(fetchAuthorizedFileDetails({ fileId, companyId }));
        dispatch(fetchUserAssets(companyId));
        if (reportId) dispatch(fetchFuelReportDetails({ companyId, reportId }));
        if (tripId && companyId) {
          dispatch(fetchBusinessTripDetails({ tripId, companyId }));
        }
      }
      // Signed: the bell and the task list of the page's company move now, not at the next refresh.
      if (paramCompanyId) dispatch(fetchCabinetSummaryThunk(paramCompanyId));
      onSuccess?.();
      setSuccessMessage("ok");
      setIsLoading(false);
      closeModal();
    } catch (e) {
      setErrMsg(clickSignErrorKey(e, "error"));
      setIsLoading(false);
    }
  };

  const closeModal = () => {
    dispatch(setIsModalxOpen(false));
    dispatch(setDestroy());
  };

  return (
    <>
      <div className="form-warnings">
        {errMsg && (
          <div>
            <Banner tone="critical">{t(errMsg)}</Banner>
          </div>
        )}
        {successMessage && (
          <div>
            <Banner tone="info">{t(successMessage)}</Banner>
          </div>
        )}
      </div>
      <Text as="h2" variant="headingMd" alignment="center">
        {t("are_you_sure_you_want_to_sign_the_file")}
      </Text>

      {/* One under the other on a phone, so "Yes" and "No" are two full-width targets and not two small buttons at the edges. */}
      <div className="flex justify-between max-md:mt-4 max-md:flex-col max-md:gap-3">
        <Button onClick={handleToSendRequest} loading={isLoading}>
          {t("Yes")}
        </Button>

        <Button onClick={closeModal}>{t("No")}</Button>
      </div>
    </>
  );
};

export default ConfirmModal;
